/**
 * Client-side chat command catalog + autocomplete.
 * Server is authoritative for execution; this only drives the picker UI.
 *
 * Future commands with args: set minArgs/maxArgs and usage like "!lobbyban <player>".
 */

/** Mirrors server games/lib/chatCommands.js canUse rules. */
export const CHAT_COMMAND_DEFS = [
  {
    name: 'ejectall',
    description: 'Clear current table — eject all players and watchers to the lobby',
    usage: '!ejectall',
    minArgs: 0,
    maxArgs: 0,
    requiresModerateLobby: true,
  },
];

/**
 * @param {{ can_moderate_lobby?: boolean, can_moderate_global?: boolean } | null} moderation
 * @param {boolean} canManageLobbies
 */
export function commandsForUser(moderation, canManageLobbies = false) {
  const canMod = !!(
    moderation?.can_moderate_lobby
    || moderation?.can_moderate_global
    || canManageLobbies
  );
  return CHAT_COMMAND_DEFS.filter((c) => {
    if (c.requiresModerateLobby) return canMod;
    return true;
  }).map((c) => ({
    name: c.name,
    description: c.description,
    usage: c.usage,
    minArgs: c.minArgs,
    maxArgs: c.maxArgs,
  }));
}

/** Merge server catalog (if present) with local defs for descriptions. */
export function mergeCommandCatalog(serverList, moderation, canManageLobbies) {
  if (Array.isArray(serverList) && serverList.length) {
    return serverList.map((c) => ({
      name: c.name,
      description: c.description || '',
      usage: c.usage || `!${c.name}`,
      minArgs: c.minArgs ?? 0,
      maxArgs: c.maxArgs ?? null,
    }));
  }
  return commandsForUser(moderation, canManageLobbies);
}

/**
 * Autocomplete after "!" + at least one letter of the command name.
 * e.g. input "!e" → [ejectall]
 * "!!" escape never suggests.
 */
export function autocompleteCommands(input, catalog) {
  const text = String(input || '');
  if (!text.startsWith('!') || text.startsWith('!!')) return [];
  const after = text.slice(1);
  // Only while typing the command name (no space yet)
  if (/\s/.test(after)) return [];
  if (after.length < 1) return [];
  const prefix = after.toLowerCase();
  const list = Array.isArray(catalog) ? catalog : [];
  return list.filter((c) => c.name.toLowerCase().startsWith(prefix));
}

/** Apply a suggestion: "!ej" + ejectall → "!ejectall " */
export function applyCommandSuggestion(input, commandName) {
  const text = String(input || '');
  if (!text.startsWith('!')) return `!${commandName} `;
  return `!${commandName} `;
}
